/**
 * 「原消息找不到」弹窗（21ah，人工指图：原来只弹一条 toast「原消息不可见或
 * 已删除」，现在直接把手上有的原消息摊开给用户看）。
 *
 * 判定仍在 MessageList（窗口内没有 → ensureMessageLoaded 补拉到头/封顶仍没
 * 有）。两处入口手上的料不一样，所以快照分两档：
 * - 置顶横幅/全部置顶 sheet：pin_list 行带着原消息的 msgType+正文 envelope，
 *   **按真实类型渲染成原样气泡**（文本/图片/贴纸/语音/视频/文件/名片都走
 *   MessageList 的 Bubble，由调用方传进来当 children）；
 * - 气泡内嵌引用块：ext.reply 里只有发送时定格的 name/text 文本快照（协议就
 *   只存这些，见 replyModel），只能显这行字。
 *
 * 零请求：只摊开手上已有的料，不为了正文再去拉服务端。模态外壳复用
 * confirmDialog.module.scss（Esc/点背景关，单「知道了」钮）。
 */
import { useLayoutEffect } from 'preact/hooks';
import type { ComponentChildren } from 'preact';
import styles from './confirmDialog.module.scss';

export interface JumpMissingSource {
  /** 原消息发送者名（空=未知，不显名字行） */
  readonly name: string;
  /** 原消息类型；0=手上只有文本快照（引用 ext），>0 由调用方渲染真气泡 */
  readonly msgType: number;
  /** 原消息正文 envelope（msgType>0 时有效） */
  readonly content: string;
  /** 文本快照/预览兜底（msgType=0 时展示；也当无正文时的判空依据） */
  readonly text: string;
}

export function JumpMissingDialog({
  source,
  bubble,
  onClose,
}: {
  source: JumpMissingSource;
  /** 真气泡（msgType>0 时由 MessageList 用 Bubble 渲染）；无=退回文本快照 */
  bubble?: ComponentChildren;
  onClose: () => void;
}) {
  useLayoutEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const hasBubble = bubble !== undefined && bubble !== null && bubble !== false;
  const hasBody = hasBubble || source.text !== '';

  return (
    <>
      <div className={styles.backdrop} data-testid="jump-missing-backdrop" onClick={onClose} />
      <div
        className={styles.dialog}
        role="alertdialog"
        aria-modal="true"
        data-testid="jump-missing"
      >
        <div className={styles.title}>原消息</div>
        {hasBody && (
          <div className={styles.quote} data-testid="jump-missing-quote">
            {source.name !== '' && <div className={styles.quoteName}>{source.name}</div>}
            {hasBubble ? (
              <div className={styles.quoteBubble}>{bubble}</div>
            ) : (
              <div className={styles.quoteText} data-testid="jump-missing-text">
                {source.text}
              </div>
            )}
          </div>
        )}
        <div className={styles.message} data-testid="jump-missing-hint">
          {hasBody
            ? '原消息已不在聊天记录里（已删除或未同步），以上是消息快照。'
            : '原消息不可见或已删除。'}
        </div>
        <div className={styles.actions}>
          <button
            type="button"
            className={styles.confirmBtn}
            data-testid="jump-missing-ok"
            onClick={onClose}
          >
            知道了
          </button>
        </div>
      </div>
    </>
  );
}
